import { FC, ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Alert } from "react-daisyui";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

import { useAuthContext } from "@/context/AuthContext.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import TextInput from "@/components/functional/TextInput/TextInput.tsx";
import Toggle from "@/components/shared/Toggle/Toggle.tsx";
import DeleteAccountModal from "@/components/modals/DeleteAccountModal/DeleteAccountModal.tsx";
import SubscriptionCancelModal from "@/components/modals/SubscriptionCancelModal/SubscriptionCancelModal.tsx";
import SubscriptionReactivateModal from "@/components/modals/SubscriptionReactivateModal/SubscriptionReactivateModal.tsx";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import Routes from "@/router/Routes.ts";
import { updateAccountSchema } from "$/requests/UpdateAccountRequest.ts";
import type { UpdateAccountRequest } from "$/requests/UpdateAccountRequest.ts";
import useUpdateUserAccount from "@/api/users/useUpdateUserAccount.ts";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import { useOptimisticState } from "@/hooks/useOptimisticState.ts";
import { passToRouteSearchParams } from "@/utils/passToRouteSearchParams.ts";
import SearchParams from "@/_constants/SearchParams.ts";

type SettingsRowProps = {
  label: ReactNode;
  value: ReactNode;
};

const SettingsRow: FC<SettingsRowProps> = ({ label, value }) => (
  <div className="grid gap-1 border-b border-black/20 py-2.5 last:border-b-0 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] sm:items-center sm:gap-5">
    <dt className="font-sans text-sm font-medium text-[#80691F] md:text-base">
      {label}
    </dt>
    <dd className="min-w-0 font-sans text-sm text-base-content sm:text-right md:text-base">
      {value}
    </dd>
  </div>
);

const SettingsPage: FC = () => {
  const {
    user,
    getSubscriptionPage,
    subscription,
    subscriptionDaysLeft,
    isSubscriptionActive,
    isTrial,
    isCancelAtPeriodEnd,
    canUpgrade,
  } = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<UpdateAccountRequest>({
    resolver: zodResolver(updateAccountSchema),
    defaultValues: {
      name: user?.name ?? undefined,
      preferred_name: user?.preferred_name ?? undefined,
      last_name: user?.last_name ?? undefined,
      email: user?.email ?? undefined,
      password: undefined,
    },
  });

  const [isCancelAtPeriodEndOptimisticState, setIsCancelAtPeriodEnd] =
    useOptimisticState(isCancelAtPeriodEnd);
  const {
    mutateAsync: updateUserAccount,
    isPending: isLoadingUpdateAccount,
    error: errorUpdateAccount,
  } = useUpdateUserAccount();

  const planLabel =
    subscription?.product.type && isSubscriptionActive
      ? `${capitalizeFirstLetter(subscription.product.name)} - ${t(
          "tripNavbar.days_remaining",
          { count: subscriptionDaysLeft },
        )}`
      : t("settingsPage.no_active_subscription");

  const handleUpdate = (data: UpdateAccountRequest) => {
    void (async () => {
      try {
        if (isLoadingUpdateAccount) return;
        if (!isDirty) {
          setIsUpdating(false);
          return;
        }

        await updateUserAccount(data);
        setIsUpdating(false);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const renderTextField = (
    name: "name" | "preferred_name" | "last_name" | "email" | "password",
    label: string,
    placeholder: string,
    type: "text" | "password" = "text",
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, formState: { errors } }) => (
        <TextInput
          type={type}
          size="sm"
          aria-label={label}
          error={errors[name]?.message}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          wrapperClassName="w-full max-w-none text-left"
          style={{ textAlign: "left" }}
        />
      )}
    />
  );

  return (
    <form
      className="min-h-[100dvh] w-full overflow-y-auto bg-journi-theme-light px-5 pb-10 pt-4 text-base-content md:px-8 lg:px-12"
      onSubmit={handleSubmit(handleUpdate)}
    >
      <div className="mx-auto w-full max-w-[1100px]">
        <header className="flex items-center gap-3 border-b border-primary/40 pb-3">
          <TripLayout.OpenMenuButton
            color="ghost"
            className="border-2 border-primary text-primary"
          />
          <button
            type="button"
            onClick={() => navigate(Routes.DASHBOARD)}
            className="font-feature text-2xl font-semibold tracking-wide"
          >
            JOURNI
          </button>
        </header>

        {errorUpdateAccount ? (
          <Alert status="error" className="mt-5">
            {errorUpdateAccount.message}
          </Alert>
        ) : null}

        <section className="mt-5 rounded-xl border border-primary bg-white px-4 py-3 md:px-5 md:py-4">
          <div className="flex items-center justify-between gap-4 border-b border-primary/40 pb-2.5">
            <h1 className="font-feature text-2xl font-semibold">
              {t("settingsPage.details")}
            </h1>
            <button
              type="button"
              className="font-sans text-sm font-semibold text-primary transition-colors hover:text-[#80691F] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              onClick={() => setIsUpdating((previous) => !previous)}
            >
              {isUpdating ? t("button.cancel") : t("settingsPage.click_to_edit")}
            </button>
          </div>

          <dl>
            <SettingsRow
              label={t("label.firstName")}
              value={
                isUpdating
                  ? renderTextField(
                      "name",
                      t("label.firstName"),
                      t("placeholder.name"),
                    )
                  : user?.name ?? "-"
              }
            />
            <SettingsRow
              label={t("label.preferredName")}
              value={
                isUpdating
                  ? renderTextField(
                      "preferred_name",
                      t("label.preferredName"),
                      t("placeholder.preferred_name"),
                    )
                  : user?.preferred_name ?? "-"
              }
            />
            <SettingsRow
              label={t("label.lastName")}
              value={
                isUpdating
                  ? renderTextField(
                      "last_name",
                      t("label.lastName"),
                      t("placeholder.last_name"),
                    )
                  : user?.last_name ?? "-"
              }
            />
            <SettingsRow
              label={t("label.email")}
              value={
                isUpdating
                  ? renderTextField(
                      "email",
                      t("label.email"),
                      t("placeholder.email"),
                    )
                  : user?.email ?? "-"
              }
            />
            <SettingsRow
              label={t("label.password")}
              value={
                isUpdating
                  ? renderTextField(
                      "password",
                      t("label.password"),
                      t("placeholder.password"),
                      "password",
                    )
                  : "******"
              }
            />
          </dl>

          {isUpdating ? (
            <div className="mt-4 flex justify-end">
              <LoadingButton loading={isLoadingUpdateAccount} color="primary">
                {t("button.save_changes")}
              </LoadingButton>
            </div>
          ) : null}
        </section>

        <section className="mt-5 rounded-xl border border-primary bg-white px-4 py-3 md:px-5 md:py-4">
          <div className="flex items-center justify-between gap-4 border-b border-primary/40 pb-2.5">
            <h2 className="font-feature text-2xl font-semibold">
              {t("label.subscription")}
            </h2>
            {(!isSubscriptionActive || isTrial) ? (
              <Link
                to={getSubscriptionPage()}
                className="font-sans text-sm font-semibold uppercase text-primary transition-colors hover:text-[#80691F] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {t("button.subscribe")}
              </Link>
            ) : null}
          </div>

          <dl>
            <SettingsRow label={t("label.plan")} value={planLabel} />
          </dl>

          {!isTrial && isSubscriptionActive ? (
            <div className="mt-4 flex flex-wrap justify-end gap-4">
              {isCancelAtPeriodEndOptimisticState ? (
                <Toggle
                  renderTrigger={({ toggle }) => (
                    <button
                      type="button"
                      className="font-sans text-sm font-semibold text-primary hover:underline"
                      onClick={toggle}
                    >
                      {t("button.reactivate")}
                    </button>
                  )}
                  renderContent={({ isOpen, handleClose }) => (
                    <SubscriptionReactivateModal isOpen={isOpen} onClose={handleClose} setIsCancelAtPeriodEnd={setIsCancelAtPeriodEnd} />
                  )}
                />
              ) : (
                <Toggle
                  renderTrigger={({ toggle }) => (
                    <button
                      type="button"
                      className="font-sans text-sm font-semibold text-primary hover:underline"
                      onClick={toggle}
                    >
                      {t("button.cancel")}
                    </button>
                  )}
                  renderContent={({ isOpen, handleClose }) => (
                    <SubscriptionCancelModal isOpen={isOpen} onClose={handleClose} setIsCancelAtPeriodEnd={setIsCancelAtPeriodEnd} />
                  )}
                />
              )}

              {canUpgrade ? (
                <Link
                  to={passToRouteSearchParams(Routes.UPGRADE, {
                    [SearchParams.REDIRECT_URL]: encodeURIComponent(window.location.href),
                  })}
                >
                  <span className="inline-flex items-center gap-1 font-sans text-sm font-semibold text-primary hover:underline">
                    {t("button.upgrade")}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Link>
              ) : null}
            </div>
          ) : null}
        </section>

        <div className="mx-2 mt-7 border-t border-primary/40 pt-5 text-center">
          <Toggle
            renderTrigger={({ toggle }) => (
              <LoadingButton type="button" color="ghost" className="font-sans font-medium uppercase text-error" onClick={toggle}>
                {t("button.delete_account")}
              </LoadingButton>
            )}
            renderContent={({ isOpen, handleClose }) => (
              <DeleteAccountModal isOpen={isOpen} onClose={handleClose} />
            )}
          />
        </div>
      </div>
    </form>
  );
};

export default SettingsPage;